/**
 * Centralized Error Handler for Server-Side Error Messages
 * 
 * This utility provides consistent error handling and toast notifications
 * across all API services in the application.
 */

import toast from 'react-hot-toast';

// Error types that can be returned by the server
export interface ServerError {
  message?: string;
  error?: string;
  businessExceptionDescription?: string;
  details?: Record<string, unknown>;
  code?: string | number;
  status?: number;
}

// Configuration for error handling behavior
interface ErrorHandlerConfig {
  showToast?: boolean;
  logError?: boolean;
  fallbackMessage?: string;
  prefix?: string;
}

// Default configuration
const DEFAULT_CONFIG: ErrorHandlerConfig = {
  showToast: true,
  logError: true,
  fallbackMessage: 'An unexpected error occurred. Please try again.',
  prefix: ''
};

/**
 * Extract meaningful error message from various server error formats
 */
export function extractErrorMessage(error: unknown): string {
  if (!error) {
    return DEFAULT_CONFIG.fallbackMessage!;
  }

  // Handle axios errors specifically (they have a response property)
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const axiosError = error as any;
    // Try to get backend error message from response data
    if (axiosError.response?.data) {
      const backendMessage = extractFromErrorResponse(axiosError.response.data);
      if (backendMessage !== DEFAULT_CONFIG.fallbackMessage) {
        return backendMessage;
      }
    }
    // If no backend message, continue to extract from Error object
    if (error instanceof Error) {
      return extractFromErrorObject(error);
    }
  }

  // Handle Error objects
  if (error instanceof Error) {
    return extractFromErrorObject(error);
  }

  // Handle response objects
  if (typeof error === 'object') {
    return extractFromErrorResponse(error as ServerError);
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  return DEFAULT_CONFIG.fallbackMessage!;
}

/**
 * Extract error message from Error object
 */
function extractFromErrorObject(error: Error): string {
  const message = error.message;

  // Handle axios error messages like "Request failed with status code 500"
  // These are not useful, so we should use a fallback message
  if (message.includes('Request failed with status code')) {
    return 'Server error occurred. Please try again or contact support if the issue persists.';
  }

  // Handle ServerError with JSON data
  if (error.name === 'ServerError') {
    try {
      const serverError = JSON.parse(message);
      return extractFromErrorResponse(serverError);
    } catch {
      // If JSON parsing fails, continue with other methods
    }
  }

  // Handle AbortError specifically
  if (error.name === 'AbortError') {
    return 'Request was cancelled. Please try again.';
  }

  // Handle network errors
  if (message.includes('Network') || message.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }

  // Handle timeout errors
  if (error.name === 'TimeoutError' || message.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }

  // Try to extract JSON from error message (common pattern in our APIs)
  if (message.includes('businessExceptionDescription') || message.includes('{')) {
    try {
      const match = message.match(/\{.*\}/);
      if (match) {
        const errorData = JSON.parse(match[0]);
        return extractFromErrorResponse(errorData);
      }
    } catch {
      // Continue with other extraction methods
    }
  }

  // Handle specific table reservation errors
  if (message.includes('Table') && message.includes('already reserved')) {
    const tableMatch = message.match(/Table '([^']+)' is already reserved on ([^"']+)/);
    if (tableMatch) {
      return `Table ${tableMatch[1]} is already reserved on ${tableMatch[2]}`;
    }
  }

  // Handle internal server errors
  if (message.includes('Internal server error') || message.includes('Internal Error')) {
    return 'Server error occurred. Please try again or contact support.';
  }

  // Handle database/session errors
  if (message.includes('no session') || message.includes('proxy') || message.includes('Database session')) {
    return 'Database connection issue. Please try again.';
  }

  // Return the original message if it seems user-friendly (not too technical)
  if (isUserFriendlyMessage(message)) {
    return message;
  }

  return DEFAULT_CONFIG.fallbackMessage!;
}

/**
 * Extract error message from server error response object
 */
function extractFromErrorResponse(errorResponse: ServerError): string {
  // Priority order for extracting error messages from backend
  // Note: We use the backend message directly without appending error codes
  const candidates = [
    errorResponse.error,
    errorResponse.businessExceptionDescription,
    errorResponse.message,
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'string' && candidate.trim()) {
      // Return the backend message directly (with minimal transformations)
      return transformToUserFriendlyMessage(candidate);
    }
  }

  // Only use status code message as last resort if no backend message is available
  if (errorResponse.status) {
    return getStatusCodeMessage(errorResponse.status);
  }

  return DEFAULT_CONFIG.fallbackMessage!;
}

/**
 * Transform technical error messages to user-friendly ones
 * Note: Preserves backend error messages without adding error codes
 */
function transformToUserFriendlyMessage(message: string): string {
  const lowerMessage = message.toLowerCase();

  // Handle table reservation conflicts (highest priority)
  // Return the backend message directly as it's already user-friendly
  if (message.includes('Table') && message.includes('already reserved')) {
    return message;
  }

  // For most backend error messages, return them directly without transformation
  // Only transform highly technical messages
  
  // Foreign key constraint violations - only if very technical
  if ((lowerMessage.includes('foreign key constraint') || 
       lowerMessage.includes('violates foreign key')) &&
      !isUserFriendlyMessage(message)) {
    return 'Cannot delete this item because it has existing reservations. Please cancel or move the reservations first.';
  }

  // Duplicate key violations - only if very technical
  if ((lowerMessage.includes('duplicate key') || 
       lowerMessage.includes('unique constraint violation')) &&
      !isUserFriendlyMessage(message)) {
    return 'An item with this name already exists. Please choose a different name.';
  }

  // If the message is already user-friendly, return as is (no error codes added)
  if (isUserFriendlyMessage(message)) {
    return message;
  }

  // For technical messages that aren't user-friendly, return fallback
  // But prefer backend message if it exists and is somewhat readable
  if (message.length > 10 && message.length < 300) {
    return message;
  }

  return DEFAULT_CONFIG.fallbackMessage!;
}

/**
 * Check if a message is user-friendly (not too technical)
 */
function isUserFriendlyMessage(message: string): boolean {
  const technicalKeywords = [
    'exception', 'stacktrace', 'null pointer', 'hibernate',
    'sql', 'database', 'connection', 'proxy', 'session',
    'org.', 'java.', 'javax.', 'springframework', 'com.'
  ];

  const lowerMessage = message.toLowerCase();
  
  // Check if message contains technical keywords
  const hasTechnicalKeywords = technicalKeywords.some(keyword => 
    lowerMessage.includes(keyword)
  );

  // Check if message is reasonably short (user-friendly messages are typically concise)
  const isReasonableLength = message.length > 10 && message.length < 200;

  return !hasTechnicalKeywords && isReasonableLength;
}

/**
 * Get user-friendly message for HTTP status codes
 */
function getStatusCodeMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input and try again.';
    case 401:
      return 'Authentication required. Please log in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource could not be found.';
    case 409:
      return 'This action conflicts with existing data. Please refresh and try again.';
    case 422:
      return 'Invalid data provided. Please check your input and try again.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'Server error occurred. Please try again later.';
    case 502:
      return 'Service temporarily unavailable. Please try again later.';
    case 503:
      return 'Service maintenance in progress. Please try again later.';
    case 504:
      return 'Request timed out. Please try again.';
    default:
      return DEFAULT_CONFIG.fallbackMessage!;
  }
}

/**
 * Handle error and optionally show toast notification
 */
export function handleApiError(
  error: unknown, 
  config: ErrorHandlerConfig = {}
): string {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const errorMessage = extractErrorMessage(error);
  
  // Add prefix if provided
  const finalMessage = finalConfig.prefix 
    ? `${finalConfig.prefix}: ${errorMessage}` 
    : errorMessage;

  // Log error if enabled
  // if (finalConfig.logError) {
  //   console.error('API Error:', error);
  //   console.error('Processed Error Message:', finalMessage);
  // }

  // Show toast notification if enabled
  if (finalConfig.showToast) {
    toast.error(finalMessage);
  }

  return finalMessage;
}

/**
 * Handle successful API response with toast
 */
export function handleApiSuccess(
  message: string, 
  showToast: boolean = true
): void {
  if (showToast) {
    toast.success(message);
  }
}

/**
 * Wrapper for API calls with automatic error handling
 */
export async function withErrorHandler<T>(
  apiCall: () => Promise<T>,
  config: ErrorHandlerConfig = {}
): Promise<T> {
  try {
    return await apiCall();
  } catch (error) {
    const errorMessage = handleApiError(error, config);
    throw new Error(errorMessage);
  }
}

/**
 * Specific error handlers for common scenarios
 */
export const ErrorHandlers = {
  // For reservation-related errors
  reservation: (error: unknown) => handleApiError(error, {
    prefix: 'Reservation Error',
    fallbackMessage: 'Failed to process reservation. Please try again.'
  }),

  // For table management errors
  table: (error: unknown) => handleApiError(error, {
    prefix: 'Table Management Error',
    fallbackMessage: 'Failed to manage table. Please try again.'
  }),

  // For authentication errors
  auth: (error: unknown) => handleApiError(error, {
    prefix: 'Authentication Error',
    fallbackMessage: 'Login failed. Please check your credentials.'
  }),

  // For general API errors without toast
  silent: (error: unknown) => handleApiError(error, {
    showToast: false
  }),

  // For critical errors that should be logged but not shown to user
  critical: (error: unknown) => {
    return handleApiError(error, {
      showToast: true,
      fallbackMessage: 'A critical error occurred. Please contact support.'
    });
  }
};

export default {
  extractErrorMessage,
  handleApiError,
  handleApiSuccess,
  withErrorHandler,
  ErrorHandlers
};